import React from 'react';
import PropTypes from 'prop-types';
import { Box, LinearProgress, styled } from '@mui/material';

// Calculate color based on progress value
const getColorFromValue = (value) => {
  if (value < 30) {
    return '#f56565'; // error color
  } else if (value < 70) {
    return '#ed8936'; // warning color
  } else {
    return '#48bb78'; // success color
  }
};

// Styled LinearProgress component
const StyledLinearProgress = styled(LinearProgress)(
  ({ theme, height = 8, value }) => {
    const progressColor = getColorFromValue(value);

    return {
      height: height,
      borderRadius: height / 2,
      backgroundColor: theme.palette.mode === 'light' ? '#e2e8f0' : '#2d3748',
      '& .MuiLinearProgress-bar': {
        borderRadius: height / 2,
        backgroundImage: `linear-gradient(90deg, 
        ${value < 50 ? '#f56565' : '#ed8936'} 0%, 
        ${progressColor} 100%)`,
        transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      },
    };
  }
);

// GradientProgress component
const GradientProgress = ({
  value = 0,
  height = 8,
  showLabel = false,
  label,
  ...props
}) => {
  // Ensure value is between 0 and 100
  const clampedValue = Math.min(Math.max(0, value), 100);

  return (
    <Box sx={{ width: '100%' }}>
      {(showLabel || label) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mb: 0.5,
            fontSize: '0.875rem',
          }}
        >
          <span>{label || 'Progress'}</span>
          <span style={{ color: getColorFromValue(clampedValue) }}>
            {clampedValue}%
          </span>
        </Box>
      )}
      <StyledLinearProgress
        variant="determinate"
        value={clampedValue}
        height={height}
        {...props}
      />
    </Box>
  );
};

GradientProgress.propTypes = {
  /**
   * The value of the progress indicator.
   * Value between 0 and 100.
   */
  value: PropTypes.number,
  /**
   * The height of the progress bar in pixels.
   */
  height: PropTypes.number,
  /**
   * If true, a label with the value will be shown.
   */
  showLabel: PropTypes.bool,
  /**
   * Custom label text.
   */
  label: PropTypes.string,
};

export default GradientProgress;
